import React from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that handles both Azure AD and OTP authentication
 * Automatically redirects unauthenticated users to login page
 * Shows loading states during authentication process
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.ReactElement {
  const { instance, accounts, inProgress } = useMsal();
  const { isAuthenticated: isOTPAuthenticated, loading: otpLoading } = useOTPAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [loginAttempted, setLoginAttempted] = React.useState(false);
  const [domainValidated, setDomainValidated] = React.useState(false);
  
  // Check if user is authenticated with either method
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  React.useEffect(() => {
    // Check if instance is already initialized
    const checkInitialization = async () => {
      try {
        // If getAllAccounts() works, it's initialized
        instance.getAllAccounts();
        setIsInitialized(true);
      } catch {
        // Not initialized, wait for it
        await instance.initialize();
        setIsInitialized(true);
      }
    };
    
    checkInitialization();
  }, [instance]);

  // Navigate to login page when needed - this hook must be at top level
  React.useEffect(() => {
    if (isInitialized && 
        inProgress === InteractionStatus.None && 
        !otpLoading &&
        !isAuthenticated && 
        !loginAttempted) {
      setLoginAttempted(true);
      // Navigate to login page with current location as return path
      navigate('/login', { state: { from: location.pathname + location.search } });
    }
  }, [isInitialized, inProgress, otpLoading, isAuthenticated, loginAttempted, navigate, location]);

  // Mark domain as validated after successful authentication
  React.useEffect(() => {
    if (isInitialized && 
        inProgress === InteractionStatus.None && 
        !otpLoading &&
        isAuthenticated && 
        !domainValidated) {
      
      // Domain validation removed - all authenticated users are allowed
      setDomainValidated(true);
    }
  }, [isInitialized, inProgress, otpLoading, isAuthenticated, accounts, domainValidated]);

  // Wait for initialization
  if (!isInitialized) {
    // Waiting for MSAL initialization
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Initializing...
        </Typography>
      </Box>
    );
  }

  // Check if authentication is in progress
  if (inProgress !== InteractionStatus.None || otpLoading) {
    // Authentication in progress
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Authenticating...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials
        </Typography>
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // No authenticated accounts found, will navigate to login page
    
    // Show loading while navigation is happening
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Check if we're validating domain
  if (!domainValidated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying permissions...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your access permissions
        </Typography>
      </Box>
    );
  }

  // User is authenticated and domain validated, render protected content
  return <>{children}</>;
}